import { useEffect, useRef, useState } from 'react';
import { WINE_WORLD_LABELS, type LookupAdminRow, type RegionKind, type WineLookupKind } from '@gwc/shared';
import { ApiError } from '../../lib/api';
import { Badge, Button } from '../ui';
import { WineColorChip } from '../WineColorChip';
import { REGION_KIND_LABELS } from './setupHelpers';

/**
 * One entry on the Setup page: tick it for bulk "Looks right", click the name
 * to fix its spelling in place, or use the actions for details, merge, delete.
 */
export function LookupRow({
  kind,
  row,
  selected,
  onSelect,
  onRename,
  onReview,
  onEdit,
  onMerge,
  onDelete,
}: {
  kind: WineLookupKind;
  row: LookupAdminRow;
  selected: boolean;
  onSelect: (on: boolean) => void;
  onRename: (name: string) => Promise<void>;
  onReview: () => void;
  onEdit: () => void;
  onMerge: (into?: { id: string; name: string }) => void;
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(row.name);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [existing, setExisting] = useState<{ id: string; name: string } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!editing) setName(row.name);
  }, [row.name, editing]);
  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  function cancel() {
    setEditing(false);
    setError(null);
    setExisting(null);
    setName(row.name);
  }

  async function save() {
    const next = name.trim().replace(/\s+/g, ' ');
    if (!next) return setError('Enter a name');
    if (next === row.name) return cancel();
    setBusy(true);
    setError(null);
    setExisting(null);
    try {
      await onRename(next);
      setEditing(false);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'lookup_exists') {
        setExisting(err.details as { id: string; name: string });
      }
      setError(err instanceof ApiError ? err.message : 'Could not rename');
    } finally {
      setBusy(false);
    }
  }

  const context = [
    row.parentName ? (kind === 'regions' ? `in ${row.parentName}` : row.parentName) : null,
    kind === 'regions' && row.regionKind ? REGION_KIND_LABELS[row.regionKind as RegionKind] ?? row.regionKind : null,
  ].filter(Boolean);

  return (
    <li className={`px-3 py-3 sm:px-4 ${selected ? 'bg-ink-900/60' : ''}`}>
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          className="mt-1.5 h-4 w-4 shrink-0 accent-gold-500"
          checked={selected}
          onChange={(e) => onSelect(e.target.checked)}
          aria-label={`Select ${row.name}`}
        />
        <div className="min-w-0 flex-1">
          {editing ? (
            <div className="flex flex-wrap items-center gap-2">
              <input
                ref={inputRef}
                className="field max-w-md flex-1 py-1.5"
                value={name}
                maxLength={200}
                aria-label={`New name for ${row.name}`}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void save();
                  if (e.key === 'Escape') cancel();
                }}
              />
              <Button className="px-3 py-1.5 text-xs" loading={busy} onClick={() => void save()}>
                Save
              </Button>
              <Button variant="quiet" className="px-2 py-1.5 text-xs" onClick={cancel}>
                Cancel
              </Button>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <button
                type="button"
                className="text-left text-cream-50 decoration-gold-500/60 underline-offset-4 hover:underline"
                title="Click to fix the spelling"
                onClick={() => setEditing(true)}
              >
                {row.name}
              </button>
              {row.needsReview ? <Badge tone="warning">Needs review</Badge> : null}
              {row.world ? <Badge tone="neutral">{WINE_WORLD_LABELS[row.world]}</Badge> : null}
              {kind === 'varietals' ? <WineColorChip color={row.color} /> : null}
            </div>
          )}
          {error ? (
            <p className="mt-1 text-xs text-danger" role="alert">
              {error}
              {existing ? (
                <button
                  type="button"
                  className="ml-2 text-gold-300 underline hover:text-gold-200"
                  onClick={() => {
                    cancel();
                    onMerge(existing);
                  }}
                >
                  Merge into {existing.name}
                </button>
              ) : null}
            </p>
          ) : null}
          <p className="mt-1 text-xs text-cream-300/55">
            {context.length ? <span>{context.join(' · ')} · </span> : null}
            {row.createdByName ? `Added by ${row.createdByName} · ` : 'Added '}
            {new Date(row.createdAt).toLocaleDateString()} ·{' '}
            {row.usageCount === 0 ? 'Unused' : `Used ${row.usageCount}×`}
            {!row.needsReview && row.reviewedByName ? ` · Checked by ${row.reviewedByName}` : null}
          </p>
        </div>
      </div>
      {!editing ? (
        <div className="mt-2 flex flex-wrap gap-x-1 gap-y-1 pl-7 text-xs">
          {row.needsReview ? (
            <Button variant="ghost" className="px-3 py-1 text-xs" onClick={onReview}>
              Looks right
            </Button>
          ) : null}
          <Button variant="quiet" className="px-2 py-1 text-xs" onClick={() => setEditing(true)}>
            Rename
          </Button>
          <Button variant="quiet" className="px-2 py-1 text-xs" onClick={onEdit}>
            Details
          </Button>
          <Button variant="quiet" className="px-2 py-1 text-xs" onClick={() => onMerge()}>
            Merge…
          </Button>
          <Button
            variant="quiet"
            className="px-2 py-1 text-xs hover:text-danger"
            disabled={row.usageCount > 0}
            title={row.usageCount > 0 ? 'In use — merge it into the right entry instead' : undefined}
            onClick={onDelete}
          >
            Delete
          </Button>
        </div>
      ) : null}
    </li>
  );
}
